import { isLoggedIn } from "@shapr3d/frontend-lib";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";

import { queryKeys } from "../config";
import { UMSrequestClient } from "../stores/requestClient";
import { TeamMembersQueryKey, useTeam } from "./useTeams";
import type { TeamsUsersResponseData } from "./useTeamsTypes";

export const TeamSpacesQueryKey = [...queryKeys.authenticatedQueryKey, "team-spaces"];
export const CreateTeamSpaceMutationKey = [...queryKeys.authenticatedQueryKey, "create-team-space"];
export const UpdateTeamSpaceMutationKey = [...queryKeys.authenticatedQueryKey, "update-team-space"];

export type TeamSpace = {
  id: string;
  name?: string;
};

export type TeamsSpacesResponseData = {
  spaces: TeamSpace[];
};

export function useTeamSpaces(teamID: string | undefined): UseQueryResult<TeamsSpacesResponseData> {
  const { isOwner, isSeatManager } = useTeam(teamID);

  return useQuery({
    queryKey: [...TeamSpacesQueryKey, teamID],
    queryFn: ({ signal }) =>
      UMSrequestClient.authenticatedRequest({
        method: "GET",
        url: `/teams/${teamID}/spaces`,
        abortSignal: signal,
      }).then((res) => {
        return res.json<TeamsUsersResponseData>();
      }),
    staleTime: Infinity,
    retry: false,
    enabled: isLoggedIn() && Boolean(isOwner || isSeatManager),
  });
}

export function useCreateTeamSpace(teamID: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [...CreateTeamSpaceMutationKey, teamID],
    mutationFn: ({ teamID, name }: { teamID: string; name: string }) =>
      UMSrequestClient.authenticatedRequest({
        method: "POST",
        url: `/teams/${teamID}/spaces`,
        body: { name },
      }).then((res) => {
        return res.json<TeamSpace>();
      }),
    onSuccess: (newSpace) => {
      queryClient.setQueryData<TeamsSpacesResponseData | undefined>(
        [...TeamSpacesQueryKey, teamID],
        (oldData) => {
          if (oldData) {
            return {
              spaces: [...oldData.spaces, newSpace],
            };
          }
          return oldData;
        }
      );
    },
  });
}

export function useUpdateTeamSpaceName(teamID: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...UpdateTeamSpaceMutationKey, teamID],
    mutationFn: ({ teamID, spaceID, name }: { teamID: string; spaceID: string; name: string }) =>
      UMSrequestClient.authenticatedRequest({
        method: "PATCH",
        url: `/teams/${teamID}/spaces/${spaceID}`,
        body: { name },
      }).then((res) => {
        return res.json<TeamSpace>();
      }),
    onSuccess: (newSpace) => {
      // Refetch the team members query to update the space name in the user object
      queryClient.refetchQueries({ queryKey: [...TeamMembersQueryKey, teamID] });

      queryClient.setQueryData<TeamsSpacesResponseData | undefined>(
        [...TeamSpacesQueryKey, teamID],
        (oldData) => {
          if (oldData) {
            return {
              spaces: [...oldData.spaces.filter((space) => space.id !== newSpace.id), newSpace],
            };
          }
          return oldData;
        }
      );
    },
  });
}
